import api from './api';
import { ApiResponse, Refuel, FuelStation, StationRequest, Fuel, FuelRequest, StationRefuelRequest, StationRefuelResponse, StationPricesRequest } from '../types';

export const refuelService = {
  // Listar todos os abastecimentos (admin)
  async getAllRefuels(): Promise<ApiResponse<Refuel[]>> {
    const response = await api.get<ApiResponse<Refuel[]>>('/refuels');
    return response.data;
  },

  // Listar abastecimentos de todos os postos do parceiro
  async getPartnerRefuels(): Promise<ApiResponse<Refuel[]>> {
    const response = await api.get<ApiResponse<Refuel[]>>('/refuels/partner/all');
    return response.data;
  },

  // Listar abastecimentos por posto específico
  async getRefuelsByStation(postoId: string): Promise<ApiResponse<Refuel[]>> {
    const response = await api.get<ApiResponse<Refuel[]>>(`/refuels/station/${postoId}`);
    return response.data;
  },

  // Listar códigos pendentes de validação
  async getPendingCodes(postoId: string): Promise<ApiResponse<Refuel[]>> {
    const response = await api.get<ApiResponse<Refuel[]>>(`/refuels/station/${postoId}/pending`);
    return response.data;
  },

  // Buscar abastecimento por ID
  async getRefuelById(id: string): Promise<ApiResponse<Refuel>> {
    const response = await api.get<ApiResponse<Refuel>>(`/refuels/${id}`);
    return response.data;
  },

  // Criar abastecimento pelo posto (novo fluxo)
  async createRefuelByStation(postoId: string, data: StationRefuelRequest): Promise<ApiResponse<StationRefuelResponse>> {
    const response = await api.post<ApiResponse<StationRefuelResponse>>(`/refuels/station/${postoId}`, data);
    return response.data;
  },

  // Listar combustíveis de um posto
  async getFuelsByStation(postoId: string): Promise<ApiResponse<Fuel[]>> {
    const response = await api.get<ApiResponse<Fuel[]>>(`/fuels/station/${postoId}`);
    return response.data;
  }
};

export const fuelService = {
  // Listar combustíveis de um posto
  async getFuelsByStation(postoId: string): Promise<ApiResponse<Fuel[]>> {
    const response = await api.get<ApiResponse<Fuel[]>>(`/fuels/station/${postoId}`);
    return response.data;
  },

  // Criar novo combustível para um posto
  async createFuel(postoId: string, data: FuelRequest): Promise<ApiResponse<Fuel>> {
    const requestData = {
      postoId: postoId,
      tipoCombustivel: String(data.tipoCombustivel),
      precoAtual: Number(data.precoAtual),
      descontoReais: Number(data.descontoReais) || 0,
      percentualCashback: Number(data.percentualCashback) || 0,
      percentualLucro: Number(data.percentualLucro) || 0,
      litrosMinimosDesconto: Number(data.litrosMinimosDesconto) || 0,
      descontoPorLitro: Number(data.descontoPorLitro) || 0
    };
    console.log('🔥 Enviando combustível para API:', JSON.stringify(requestData, null, 2));
    const response = await api.post<ApiResponse<Fuel>>('/fuels', requestData);
    console.log('✅ Resposta da API:', response.data);
    return response.data;
  },

  // Atualizar combustível
  async updateFuel(fuelId: string, data: FuelRequest, postoId?: string): Promise<ApiResponse<Fuel>> {
    const requestData = {
      postoId: postoId || '',
      tipoCombustivel: data.tipoCombustivel,
      precoAtual: data.precoAtual,
      descontoReais: data.descontoReais || 0,
      percentualCashback: data.percentualCashback || 0,
      percentualLucro: data.percentualLucro || 0,
      litrosMinimosDesconto: data.litrosMinimosDesconto || 0,
      descontoPorLitro: data.descontoPorLitro || 0
    };
    const response = await api.put<ApiResponse<Fuel>>(`/fuels/${fuelId}`, requestData);
    return response.data;
  },

  // Excluir combustível
  async deleteFuel(fuelId: string): Promise<ApiResponse<void>> {
    const response = await api.delete<ApiResponse<void>>(`/fuels/${fuelId}`);
    return response.data;
  }
};

export const stationService = {
  // Listar meus postos (parceiro)
  async getMyStations(): Promise<ApiResponse<FuelStation[]>> {
    const response = await api.get<ApiResponse<FuelStation[]>>('/stations/my');
    return response.data;
  },

  // Buscar posto por ID
  async getStationById(id: string): Promise<ApiResponse<FuelStation>> {
    const response = await api.get<ApiResponse<FuelStation>>(`/stations/${id}`);
    return response.data;
  },

  // Criar novo posto
  async createStation(data: StationRequest): Promise<ApiResponse<FuelStation>> {
    const response = await api.post<ApiResponse<FuelStation>>('/stations', data);
    return response.data;
  },

  // Atualizar posto
  async updateStation(id: string, data: StationRequest): Promise<ApiResponse<FuelStation>> {
    const response = await api.put<ApiResponse<FuelStation>>(`/stations/${id}`, data);
    return response.data;
  },

  // Atualizar preços do posto (responsável pelo posto)
  async updatePrices(id: string, data: StationPricesRequest): Promise<ApiResponse<Fuel[]>> {
    const response = await api.put<ApiResponse<Fuel[]>>(`/stations/${id}/prices`, data);
    return response.data;
  },

  // Enviar/substituir foto do posto
  async uploadPhoto(id: string, file: File): Promise<ApiResponse<FuelStation>> {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post<ApiResponse<FuelStation>>(`/stations/${id}/photo`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Remover foto do posto
  async deletePhoto(id: string): Promise<ApiResponse<FuelStation>> {
    const response = await api.delete<ApiResponse<FuelStation>>(`/stations/${id}/photo`);
    return response.data;
  },

  // Excluir posto
  async deleteStation(id: string): Promise<ApiResponse<void>> {
    const response = await api.delete<ApiResponse<void>>(`/stations/${id}`);
    return response.data;
  },

  // Listar todos os postos (público)
  async getAllStations(): Promise<ApiResponse<FuelStation[]>> {
    const response = await api.get<ApiResponse<FuelStation[]>>('/stations');
    return response.data;
  }
};

